// packages/shared/src/types/models.ts
import type { ClinicalSpecialty, SupervisionLevel, EmploymentType } from './models/users';
import type { ERACharge } from './models/billing';
export type { Case } from './models/clinical';
export type { ClinicalSpecialty, SupervisionLevel, EmploymentType } from './models/users';
export type { CalendarLegendSettings, CalendarDisplaySettings, SchedulingRule, SchedulingRuleVisitPattern } from './models/scheduling';

export type TexturePattern = 'dots' | 'diagonal_lines' | 'crosshatch' | 'horizontal_lines' | 'none';

/**
 * An alternate legal billing entity (NPI + Tax ID pair) configured in
 * organization.settings.billing_entities. Selected ad hoc at claim generation;
 * any field left unset inherits the organization's base value.
 */
export interface OrgBillingEntity {
  id: string; // stable UUID generated at creation; referenced by claim generation
  label: string; // display name shown in the billing entity selector
  name?: string; // legal business name → bill_name
  npi_type2?: string; // → bill_npi
  tax_id?: string; // → bill_taxid
  taxonomy_code?: string;
  medicare_provider_number?: string;
  bcbs_provider_number?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}

export interface Organization {
  id: string;
  created_at: number;
  updated_at: number;
  name: string;
  type: string;
  size: string;
  ehr_system?: string;
  location_indicator?: string;
  fax?: string;
  dea_registered_institution?: boolean;
  specialties?: ClinicalSpecialty[];
  npi_type2?: string;
  tax_id?: string;
  phone?: string;
  medicare_provider_number?: string;
  bcbs_provider_number?: string;
  taxonomy_code?: string;
  settings: {
    /** Patient Feedback feature (Organization page card). Shows the Feedback dashboard,
     *  the feedback form type, and the Feedback form-settings card. Default off; client-side gate. */
    feedback_enabled?: boolean;

    /** Single Sign-On (Organization page card). Users whose email domain is in
     *  `domains` sign in through the org's Auth0 enterprise connection: invite /
     *  activate / resend send a "sign in with <provider_label>" email instead of a
     *  set-password link, and admin password-set is refused for them
     *  (backend/functions/resolve_sso_user.xs). The Auth0 connection and its
     *  email-domain routing are configured in the Auth0 dashboard, not here. */
    sso?: {
      enabled?: boolean;
      provider_label?: string;
      domains?: string[];
    };

    // Scheduling settings (moved from provider_availability)
    scheduling_mode?: 'auto_confirm' | 'require_approval';
    allow_time_adjustment?: boolean;
    default_duration_minutes?: number;
    advance_booking_days?: number;
    minimum_notice_hours?: number;
    allow_flexible_scheduling?: boolean;
    buffer_minutes?: number;
    /** Hours before start inside which patients can no longer cancel OR reschedule online (link + portal). 0 = none. Default 24. */
    cancellation_window_hours?: number;
    max_per_slot?: number;
    // Online self-service (2026-10-21). Absent = current behavior.
    /** Which actions the reminder-link page offers. Portal unaffected. */
    self_service_link_actions?: 'cancel_and_reschedule' | 'cancel_only' | 'reschedule_only';
    /** "case_providers": online reschedule offers only providers with a non-cancelled appointment on the same case (no case = current provider only). */
    self_service_reschedule_provider_scope?: 'eligible' | 'case_providers';
    /** Reschedule picker scans today through Saturday of next week and offers the waitlist when nothing is open. */
    self_service_waitlist_on_no_availability?: boolean;
    /** Online cancels (always outside the window) are recorded as deletions: row stamped + removed like a staff delete, excluded from cancellation stats, still logged and still confirmed to the patient. */
    self_service_cancel_records_deletion?: boolean;
    /** Online reschedules keep the appointment's location (no location picker). */
    self_service_same_location_only?: boolean;
    /** Online booking / reschedule pickers hide times covered by a calendar note (schedule_override type 'note'); a blockless note hides the whole day. Absent/false = notes ignored online (in-app scheduler always treats notes as a soft block). */
    self_service_notes_block_online?: boolean;

    // Billing settings
    billing_mode?: 'cash' | 'insurance_only' | 'hybrid';
    require_completed_for_billing?: boolean;
    /** Default off. Nightly preparation of unsubmitted claims for insurance/hybrid organizations; excludes Veradigm PM. */
    auto_generate_claims_overnight?: boolean;
    auto_adjust_remainder?: boolean;
    auto_save_eligibility_to_insurance?: boolean;
    superbill_invoicing_enabled?: boolean;
    /** Superbills price line items from intervention_catalog default_rate (falls back to stored charge when no catalog match). Absent = enabled. */
    superbill_charges_from_catalog?: boolean;
    referring_provider_optional?: boolean;
    /** Default off. Shows the "Modifiers for this case" picker in CaseForm (case.billing_modifiers). Gates the UI only: stored case modifiers are applied to procedure codes in the note editor and at billing regardless of this setting. */
    case_billing_modifiers?: boolean;
    /** Dollars subtracted from the CMS KX therapy threshold ($2,480) so KX auto-apply and cap warnings trigger earlier. When set (> 0) it applies to all Medicare patients and overrides per-insurance therapy_spending.kx_threshold. Absent/0 = CMS cap (per-insurance kx_threshold honored). */
    kx_threshold_offset?: number;
    billing_entities?: OrgBillingEntity[];
    /** Personal injury billing: claim generation reads the accident date from the case's signed initial evaluation (template field with claimMapping.target = 'accident_date'); a found date sets Box 10 (10a/10b by plan_type, else 10c) and Box 14. */
    personal_injury_billing?: boolean;
    /** Expected revenue model cutover (YYYY-MM-DD). Encounters with DOS before this date keep the legacy 100%-of-Medicare expected; on/after get the v2 model (per-payer % of Medicare, MPPR, CQ/CO assistant reduction, sequestration). Absent = v2 for all DOS. See utils/expectedReimbursement.ts. */
    expected_model_effective_date?: string;
    use_billing_address?: boolean;
    billing_address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      zip?: string;
      phone?: string;
      fax?: string;
    };

    // EHR settings
    use_ehr?: boolean;
    uses_cases?: boolean;
    case_specialties?: ClinicalSpecialty[] | null;
    /** When true, form sends carry a case_id: the send modal shows a case selector, wizard/referral intakes stamp the booked case, the Cases tab shows a per-case Forms card, and Form Send History shows a Case column. Absent/false = forms are patient-wide across all cases (multispecialty default). Frontend-only gate via caseScopedForms(); backend paths are null-safe. */
    case_scoped_forms?: boolean;
    /** Child of case_scoped_forms. When true, SendFormPatientModal drops the "No case" option and blocks Send until an active case is chosen (patients with no active case cannot be sent forms from Patient Details). Frontend-only gate. Default off. */
    require_form_case?: boolean;
    require_case_referring_provider?: boolean;
    referring_provider_specialties?: ClinicalSpecialty[] | null;
    /** When true, cases billed to insurance must have at least one body region or functional area (CaseForm, PatientForm create-case, ReferralConvertWizard). Default off. */
    require_case_body_regions?: boolean;
    company_holidays?: Array<{ date: string; name: string }>;
    /** Admin > Organization > Productivity Basis card. When true the reporting work-hours walk counts company_holidays dates as zero hours (and zero visit capacity). Default off. */
    reporting?: {
      deduct_company_holidays?: boolean;
      /** User uuids removed from reporting totals, averages and hours denominators (still selectable as a provider filter). */
      excluded_user_ids?: string[];
    };

    // Clinical settings
    care_team_enabled?: boolean;

    // Documentation settings (edited in components/settings/DocumentationSettings.jsx)
    documentation?: {
      /** Note completion-rate metric: a visit counts as complete when signed within this many days of DOS. Default 2. */
      completion_threshold_days?: number;
      business_days_only?: boolean;
      use_company_holidays?: boolean;
      /** Progress-note milestones (calendar PN-due shading, Start Visit forcing, chart/encounter milestone alerts) count only SIGNED progress_note/reassessment notes; a PTA's own signature counts, cosignature is not required. Also enables the overdue branch for once-recurrence rules. Absent/false = any note (draft included) satisfies the milestone. */
      pn_due_signed_only?: boolean;
      /** When the case is PN-due (same predicate as the calendar shading, utils/pnDue.js), CreateVisitModal limits the template picker to ANY of these note types (OR) at every enforcement level. Absent/empty = only hard_block rule violations narrow the picker (legacy mapping). */
      pn_due_forced_note_types?: Array<'progress_note' | 'reassessment' | 'discharge_summary'>;
      /** Plan-of-care certification alerts (expired-POC PN-due shading and Start Visit forcing, POC expiry text on calendar/Home, poc_expiration rule violations, chart POC alerts) apply only to cases whose billing coverage (resolveCaseBillingCoverage) is medicare or medicare_advantage. Visit milestones unaffected. Absent/false = every patient. */
      poc_alerts_medicare_only?: boolean;
    };

    // Home Health agency settings (components/settings/HomeHealthSettings.jsx; solution 24)
    home_health?: {
      /** Physician orders are due back signed within this many days of being received. Default 30. Read by functions/hh_order_signature_due. */
      order_signature_days?: number;
      /** Medicare CCN (OASIS M0010, claims) */
      ccn?: string;
      /** Two-letter state (OASIS STATE_CD) */
      state_code?: string;
      /** HHA_AGENCY_ID on OASIS submissions */
      iqies_agency_id?: string;
      /** OASIS M0016 */
      branch_id?: string;
      /** OASIS M0014 */
      branch_state?: string;
      /** Value code 61 fallback on home health claims */
      default_cbsa?: string;
      /** Value code 85 on home health claims */
      default_fips?: string;
    };

    // Integration settings
    google_calendar_enabled?: boolean;

    // Calendar legend settings
    calendar_legend?: CalendarLegendSettings;

    // Calendar display settings
    calendar_display?: CalendarDisplaySettings;

    // Scheduling rules
    scheduling_rules?: import('./models/scheduling').SchedulingRule[];

    // Reporting weights
    cpt_code_weights?: Record<string, number>;
    default_cpt_weight?: number;

    // Patient invite settings
    patient_invite_preferred_form_id?: string;
    required_practice_form_ids?: string[];
    required_practice_forms?: Array<{
      form_id: string;
      include_in_alerts?: boolean;
      /**
       * Alert-only gate: keys AND together, values within a key ANY-match.
       * payer_ids = organization_payer ids, payer_group_ids = payer_group ids;
       * those two are ONE dimension (a policy matching either satisfies it).
       */
      conditions?: { plan_types?: string[]; body_regions?: string[]; payer_ids?: string[]; payer_group_ids?: string[] };
    }>;

    // Classes settings
    uses_classes?: boolean;
    class_session_generation_weeks?: number; // Rolling window for auto-generating class sessions (default 4)

    // Waitlist
    uses_waitlist?: boolean;

    // Home health + outpatient dual care setting
    uses_hh_and_outpatient?: boolean;

    // Mileage tracking
    track_mileage?: boolean;

    // Time tracking settings
    time_tracking?: {
      enabled?: boolean;
      required_employment_types?: EmploymentType[];
      /** Employees may submit PTO requests (Profile + calendar); admins review on PTO Requests */
      pto_enabled?: boolean;
      /** manual (default): balance = last entered opening balance minus usage; per_pay_period: + pto_hours_annual/26 per 14 days since as_of */
      pto_accrual_method?: 'manual' | 'per_pay_period';
      /** Days after hire_date (fallback created_at) before an employee may submit a PTO request; 0/absent = no wait */
      pto_eligibility_days?: number;
      /** false blocks requests that would take the available balance below zero (default true) */
      pto_allow_negative?: boolean;
      /** Cap on opening + accrued hours (snapshot mode); null = no cap */
      pto_max_balance_hours?: number | null;
    };

    // Patient onboarding settings
    patient_onboarding?: {
      enabled_steps?: string[];
    };

    // Offline mode
    /** Provider-app offline mode, the permanent flag. Bundles older than 2026-09-17 read ONLY this key and run the old, much heavier prefetch. */
    offline_mode?: boolean;
    /** Rollout guard: enables offline mode for current bundles only (either key enables). See docs/OFFLINE_MODE.md. */
    offline_mode_v2?: boolean;

    // Branding
    branding?: {
      logo_url?: string;
      primary_color?: string;
      hide_name_with_logo?: boolean;
    };

    // Document header configuration (per-document-type toggles)
    document_header?: {
      visit_summary?: {
        use_location_address?: boolean;
        show_fax?: boolean;
        show_insurance?: boolean;
        show_referring_provider?: boolean;
        show_time_in_out?: boolean;
        show_procedure_codes?: boolean;
      };
      plan_of_care?: {
        show_fax?: boolean;
        show_insurance?: boolean;
        show_referring_provider?: boolean;
      };
      invoice?: {
        show_fax?: boolean;
        show_insurance?: boolean;
      };
      explanation_of_benefits?: {
        show_fax?: boolean;
        show_insurance?: boolean;
        /** Prefills the Additional Comments box in the EOB modal; staff can edit or clear per document */
        default_comments?: string;
      };
    };
  };
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  subscription?: {
    type: string;
    details?: string;
    amount_and_frequency?: string;
    management_link?: string;
  };
  baa_accepted?: boolean;
  baa_accepted_at?: number;
  baa_accepted_by?: string;
  sla_accepted?: boolean;
  sla_accepted_at?: number;
  sla_accepted_by?: string;
}


// Patient Model - Comprehensive EHR Version

export interface Patient {
  // Existing fields
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  suffix?: string;
  birthday: string; // YYYY-MM-DD format
  gender: 'M' | 'F' | 'U';
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
  };
  phone?: string;
  organization_id: string;
  provider_id?: string; // Primary provider
  pharmacy_ncpdp_id?: string;

  // New fields for comprehensive EHR
  user_id?: string; // For patient portal access
  email?: string;
  preferred_contact_method?: 'phone' | 'email' | 'text' | 'portal';
  preferred_name?: string; // Nickname / "goes by"
  preferred_pronouns?: string;

  // Enhanced demographics
  ssn_last_four?: string; // Only last 4 digits for identification
  drivers_license_state?: string;
  ethnicity?: string;
  race?: string[];
  preferred_language?: string;
  marital_status?: 'Single' | 'Married' | 'Divorced' | 'Widowed' | 'Separated' | 'Partnered';

  // Clinical Information
  allergies: {
    substance: string;
    reaction?: string;
    severity?: 'mild' | 'moderate' | 'severe' | 'life_threatening';
    onset_date?: string;
    notes?: string;
  }[];

  medical_history: {
    condition: string;
    icd10_code?: string;
    diagnosed_date?: string;
    resolved_date?: string;
    status: 'active' | 'resolved' | 'chronic';
    notes?: string;
  }[];

  current_medications: {
    name: string;
    dosage?: string;
    frequency?: string;
    route?: string;
    start_date?: string;
    end_date?: string;
    prescribed_by?: string;
    pharmacy?: string;
    is_active: boolean;
    notes?: string;
  }[];

  surgical_history: {
    procedure: string;
    date?: string;
    surgeon?: string;
    facility?: string;
    complications?: string;
    notes?: string;
  }[];

  family_history: {
    relationship: string;
    condition: string;
    age_at_diagnosis?: number;
    age_at_death?: number;
    notes?: string;
  }[];

  social_history: {
    smoking_status: 'never' | 'former' | 'current' | 'unknown';
    smoking_pack_years?: number;
    smoking_quit_date?: string;
    alcohol_use: 'none' | 'social' | 'moderate' | 'heavy' | 'unknown';
    alcohol_notes?: string;
    substance_use?: string;
    exercise_frequency: 'none' | 'occasional' | 'regular' | 'daily';
    exercise_notes?: string;
    occupation?: string;
    education_level?: string;
    living_situation?: string;
    safety_concerns?: string;
  };

  // Vitals (most recent)
  vitals?: {
    height?: number; // inches
    weight?: number; // pounds
    bmi?: number;
    blood_pressure_systolic?: number;
    blood_pressure_diastolic?: number;
    heart_rate?: number;
    temperature?: number; // fahrenheit
    respiratory_rate?: number;
    oxygen_saturation?: number;
    pain_level?: number; // 0-10
    updated_at: string;
  };

  // Emergency & Insurance
  emergency_contacts: {
    name: string;
    relationship: string;
    phone: string;
    phone_type?: 'mobile' | 'home' | 'work';
    email?: string;
    is_primary: boolean;
  }[];

  insurance: {
    payer_name: string;
    payer_id?: string;
    plan_name?: string;
    plan_type?: 'medicare' | 'medicare_advantage' | 'medicaid' | 'commercial_ppo' | 'commercial_hmo' | 'commercial_epo' | 'commercial_pos' | 'commercial_other' | 'workers_comp' | 'auto' | 'tricare' | 'self_pay' | 'other' | string;
    member_id: string;
    group_number?: string;
    is_primary: boolean;
    subscriber_first_name?: string;
    subscriber_last_name?: string;
    subscriber_relationship?: 'self' | 'spouse' | 'child' | 'other';
    subscriber_dob?: string;
    subscriber_gender?: 'M' | 'F' | 'U';
    subscriber_address?: { line1?: string; line2?: string; city?: string; state?: string; zip?: string; };
    effective_date?: string;
    termination_date?: string;
    renewal_date?: string;
    copay_amount?: number | 'NA';
    coinsurance_rate?: number | 'NA'; // percentage 0-100
    deductible_amount?: number | 'NA';
    deductible_met?: number;
    oop_max_amount?: number | 'NA';
    oop_max_met?: number;

    // Claim.MD payer mapping
    claimmd_payer_id?: string;
    // Stable link to the organization_payer row (uuid), stamped at insurance write time.
    // Read paths resolve by this id first, falling back to claimmd_payer_id / name matching.
    /** Stable policy handle used by case billing coverage; retained across edits and PM sync. */
    insurance_entry_id?: string;
    /** Source policy identity from Veradigm GetPatientPolicy. */
    veradigm_policy_id?: string;
    organization_payer_id?: string;

    // Workers comp fields
    workers_comp_claim_number?: string;
    employer_name?: string;
    employer_address?: { line1?: string; city?: string; state?: string; zip?: string; };
    accident_date?: string;
    accident_state?: string;
    accident_description?: string;
    authorization_number?: string;
    requires_prior_auth?: boolean;
    /** Payer requires a referral — distinct from prior authorization */
    requires_referral?: boolean;
    /** Only with requires_referral: the referral must come from the patient's PCP (gatekeeper HMO, MA HMO, TRICARE Prime). Copy-only in v1. */
    requires_pcp_referral?: boolean;
    note?: string;

    // Eligibility cache
    eligibility_status?: 'active' | 'inactive' | 'unknown';
    /** Who set the status: clearinghouse check result vs staff override. Checks never overwrite a 'manual' status. */
    eligibility_status_source?: 'clearinghouse' | 'manual';
    eligibility_status_manual_by?: string;
    eligibility_status_manual_at?: number;
    eligibility_checked_at?: number;
    /**
     * Stamped when the most recent clearinghouse check for this entry was rejected
     * (e.g. ClaimMD 58 Invalid/Missing DOB). Status, benefits and eligibility_checked_at
     * are left untouched on such a check; cleared by the next successful check.
     */
    eligibility_last_error?: { at: number; messages: string[]; codes: (string | null)[] };
    /** @deprecated Use eligibility_checks table instead. Will be removed from patient.insurance. */
    eligibility_raw_benefits?: object;
    eligibility_benefits?: {
      insurance_plan?: string;
      insurance_type?: string;

      // Deductibles
      individual_deductible_in_network?: number;
      individual_deductible_in_network_remaining?: number;
      individual_deductible_out_of_network?: number;
      individual_deductible_out_of_network_remaining?: number;
      family_deductible_in_network?: number;
      family_deductible_in_network_remaining?: number;
      family_deductible_out_of_network?: number;
      family_deductible_out_of_network_remaining?: number;

      // Out-of-pocket maximums
      individual_oop_max_in_network?: number;
      individual_oop_max_in_network_remaining?: number;
      individual_oop_max_out_of_network?: number;
      individual_oop_max_out_of_network_remaining?: number;
      family_oop_max_in_network?: number;
      family_oop_max_in_network_remaining?: number;
      family_oop_max_out_of_network?: number;
      family_oop_max_out_of_network_remaining?: number;

      // Copays by service type (keyed by benefit_code: '98'=office visit, '96'=specialist, 'PT'=physical therapy, etc.)
      copays_in_network?: Record<string, { amount: number; description: string; notes?: string; }>;
      copays_out_of_network?: Record<string, { amount: number; description: string; notes?: string; }>;

      // Coinsurance by service type
      coinsurance_in_network?: Record<string, { percent: number; description: string; }>;
      coinsurance_out_of_network?: Record<string, { percent: number; description: string; }>;

      // Active coverage types
      covered_services?: string[];

      // Medicare Part A/B
      medicare_part_a_deductible?: number;
      medicare_part_a_deductible_remaining?: number;
      medicare_part_b_deductible?: number;
      medicare_part_b_deductible_remaining?: number;
      medicare_part_b_coinsurance?: number;
      lifetime_reserve_days?: number;
      lifetime_reserve_days_remaining?: number;
      therapy_spending?: { service: string; amount: number; }[];

      // Visit/service limitations
      limitations?: {
        service: string;
        benefit_code: string;
        quantity_allowed: number;
        quantity_remaining: number;
        period: string;
        network: string;
        notes?: string;
      }[];
    };

    // Per-specialty benefit summary (populated by multi-call eligibility check)
    specialty_benefits?: Record<string, {
      service_code: string;
      specialty_label: string;
      copay_in_network?: number;
      copay_out_of_network?: number;
      coinsurance_in_network?: number;
      coinsurance_out_of_network?: number;
      visits_allowed?: number;
      visits_remaining?: number;
      authorization_required?: boolean;
      deductible_applies?: boolean;
      service_covered_in_network?: boolean;
      service_covered_out_of_network?: boolean;
    }>;

    // Therapy spending thresholds (Medicare)
    therapy_spending?: {
      pt_slp?: { amount: number; as_of?: string; };
      ot?: { amount: number; as_of?: string; };
      kx_threshold?: number;
      tmr_threshold?: number;
    };

    // Visit limits per specialty (manually entered benefit caps).
    // Keys come from VISIT_LIMIT_SPECIALTIES (constants/insurance.ts),
    // including pt_ot_combined for limits shared across both disciplines.
    visit_limits?: Record<string, {
      allowed: number | string;
      period: 'calendar_year' | 'plan_year' | 'per_episode' | 'lifetime' | 'per_condition' | 'custom';
      period_start?: string; // YYYY-MM-DD renewal anniversary for plan_year / per_condition / per_episode; absent = policy renewal_date / termination_date / effective_date, else Jan 1
      period_label?: string; // free-text label, only for period === 'custom'
      reported?: number; // payer-reported total used (typed from a benefits call). patient-visit-limit-usage adds Prism visits dated on or after reported_as_of.
      reported_as_of?: string; // YYYY-MM-DD the payer gave that total; defaults to the day it was typed. A date before the benefit window makes the report stale (ignored).
      /** @deprecated v3 "visits used outside Prism"; no longer read since 10/21. */
      used?: number;
      max_type?: 'hard' | 'soft';
      can_request_more?: boolean; // payer may grant more visits on request
    }>;

    // Deductible met status (derived from eligibility check)
    deductible_met_in_network?: boolean;
    deductible_met_out_of_network?: boolean;

    // Multi-insurance coordination
    coordination_of_benefits_order?: number; // 1=primary, 2=secondary, 3=tertiary
  }[];

  // Guarantor / Responsible Party
  guarantor?: {
    relationship: 'self' | 'spouse' | 'parent' | 'child' | 'sibling' | 'other';
    relationship_other?: string;
    roles: ('guarantor' | 'poa_healthcare' | 'poa_financial' | 'legal_guardian' | 'conservator' | 'custodial_parent')[];
    first_name: string;
    last_name: string;
    date_of_birth?: string;
    gender?: 'M' | 'F' | 'U';
    phone?: string;
    email?: string;
    address?: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      zip: string;
    };
    legal_documentation?: {
      document_type: 'poa_healthcare' | 'poa_financial' | 'guardianship_order' | 'conservatorship_order' | 'custody_order' | 'other';
      document_type_other?: string;
      date_signed?: string;
      expiration_date?: string;
      notes?: string;
      on_file: boolean;
    }[];
  };

  // Care Team
  care_team: {
    provider_id: string;
    provider_name?: string; // Denormalized for display
    role: 'primary' | 'specialist' | 'therapist' | 'pcp' | 'referring' | 'other';
    specialty?: string;
    start_date?: string;
    end_date?: string;
    is_active: boolean;
    notes?: string;
  }[];

  // Consent & Preferences
  consents: {
    type: 'treatment' | 'billing' | 'communication' | 'research' | 'photography';
    granted: boolean;
    date: string;
    expires_date?: string;
    notes?: string;
  }[];

  communication_preferences: {
    appointment_reminders: boolean;
    health_tips: boolean;
    billing_notifications: boolean;
    lab_results: boolean;
    prescription_refills: boolean;
  };

  sms_preferences?: boolean;
  email_preferences?: boolean;
  notification_preferences?: boolean;

  // Lifestyle
  lifestyle_data?: {
    sleep: {
      average_hours: number;
      quality: 'poor' | 'fair' | 'good' | 'excellent';
      sleep_issues?: string[];
      bedtime_routine?: string;
    };

    diet: {
      tracking_level: 'none' | 'basic' | 'detailed' | 'macros';
      // Basic level
      eating_pattern?: 'regular' | 'irregular' | 'restricted';
      dietary_preferences?: string[]; // vegetarian, vegan, etc.
      food_allergies?: string[]; // separate from medical allergies

      // Detailed level
      meals_per_day?: number;
      water_intake?: number; // glasses per day

      // Macros level
      daily_calories?: number;
      protein_grams?: number;
      carbs_grams?: number;
      fat_grams?: number;
    };

    stress: {
      level: 'low' | 'moderate' | 'high' | 'severe';
      sources?: string[]; // work, family, health, financial
      management_techniques?: string[];
    };

    activity: {
      level: 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active';
      exercise_frequency?: string; // "3x per week", "daily", etc.
      current_activities?: string[];
      steps_per_day?: number;
    };

    updated_at: string;
  };

  // Fitness
  fitness_profile?: {
  // === Existing fields ===
  goals: (
    | 'weight_loss' 
    | 'muscle_gain' 
    | 'endurance' 
    | 'strength' 
    | 'flexibility' 
    | 'general_fitness'
    | 'sport_specific'
    | 'rehabilitation'
    | 'stress_relief'
    | 'improve_energy'
  )[];
  fitness_level: 'beginner' | 'intermediate' | 'advanced';
  physical_condition?: 'very_low' | 'low' | 'moderate' | 'good' | 'high';
  preferred_duration: number; // minutes per session
  available_equipment: string[];
  physical_limitations?: string;
  
  // === New: Cardio Preferences ===
  cardio_preferences?: {
    preferred_activities: (
      | 'running' 
      | 'cycling' 
      | 'swimming' 
      | 'rowing' 
      | 'elliptical' 
      | 'walking' 
      | 'hiking' 
      | 'jump_rope' 
      | 'stair_climbing'
      | 'dancing'
      | 'sports'
      | 'other'
    )[];
    outdoor_preference: 'indoor_only' | 'outdoor_only' | 'both';
    has_cardio_equipment?: string[]; // 'treadmill', 'bike', 'rower', etc.
  };
  
  // === New: Training Preferences ===
  training_preferences?: {
    preferred_workout_types: (
      | 'strength' 
      | 'cardio' 
      | 'hiit' 
      | 'yoga' 
      | 'pilates' 
      | 'functional' 
      | 'bodyweight' 
      | 'mixed'
      | 'circuit'
    )[];
    preferred_days: number[]; // ISO days 1-7 (1=Monday, 7=Sunday)
    preferred_time_of_day?: 'morning' | 'afternoon' | 'evening' | 'flexible';
    sessions_per_week: number;
  };
  
  // === New: Experience & Background ===
  experience?: {
    years_training?: number;
    sports_background?: string[];
    previous_programs?: string[]; // 'crossfit', 'powerlifting', 'p90x', etc.
  };
  
  // === New: Health Screening (for standalone users) ===
  health_screening?: {
    // PAR-Q style questions
    has_heart_condition: boolean;
    has_chest_pain_during_activity: boolean;
    has_chest_pain_at_rest: boolean;
    has_balance_or_dizziness: boolean;
    has_bone_or_joint_problems: boolean;
    takes_blood_pressure_medication: boolean;
    has_other_medical_reasons: boolean;
    other_medical_details?: string;
    
    // Additional screening
    has_current_injuries: boolean;
    current_injuries?: string[];
    has_chronic_conditions: boolean;
    chronic_conditions?: string[];
    
    // Clearance
    physician_clearance_obtained?: boolean;
    physician_clearance_date?: string;
    
    // Status
    screening_passed: boolean;
    requires_medical_clearance: boolean;
    notes?: string;
    completed_at: string;
  };
  
  // Timestamps
  created_at: string;
  updated_at: string;
};

  // Portal Access
  portal_access_enabled: boolean;
  portal_registration_date?: string;
  portal_last_login?: string;

  // Administrative
  status: 'active' | 'inactive' | 'deceased';
  inactive_reason?: string;
  inactive_date?: string;
  deceased_date?: string;

  // Clinical Flags
  flags: {
    type: 'allergy' | 'fall_risk' | 'infection_control' | 'behavioral' | 'other';
    description: string;
    severity: 'low' | 'medium' | 'high';
    created_date: string;
    created_by: string;
    is_active: boolean;
  }[];

  // Notes
  internal_notes?: string; // Not visible to patient
  needs_prior_authorization?: boolean;

  // Billing
  stripe_customer_id?: string;
  default_payment_method_id?: string;

  // Timestamps
  updated_at?: string;
  last_visit_date?: string;
  next_appointment_date?: string;
}

export interface PatientFeatures {
  patient_id?: string;
  provider_id?: string;
  organization_id?: string;
  access_type: 'provider_linked' | 'standalone' | 'hybrid';
  provider_linked: boolean;
  
  // Platform context (NEW)
  platform: 'ehr' | 'exercise' | 'rehab' | 'formbuilder';
  
  enabled_features: {
    appointments: boolean;
    clinical_records: boolean;
    messaging: boolean;
    prescriptions: boolean;
    forms: boolean;
    rehab_programs: boolean;
    workout_plans: boolean;
    self_guided_workouts: boolean;
    community_features: boolean;
    // NEW features
    create_own_workouts: boolean;
    progress_tracking: boolean;
    orders: boolean;
    documents: boolean;
    classes: boolean;
    standalone_exercises?: boolean;
  };
  
  subscription?: {
    plan: 'free' | 'basic' | 'premium';
    status: 'active' | 'trial' | 'expired' | 'cancelled';
    features: string[];
    expires_at?: string;
  };
}

export interface ExerciseAppProfile {
  // Subscription & Access
  subscription_status: 'none' | 'trial' | 'active' | 'past_due' | 'cancelled' | 'access_code';
  subscription_plan?: 'monthly' | 'annual' | 'lifetime';
  subscription_started_at?: string;
  subscription_expires_at?: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  /** ISO; end of the current Stripe billing period (display only, written by stripe/main-webhook). */
  current_period_end?: string;
  /** Stripe cancel_at_period_end flag, written by stripe/main-webhook. */
  cancel_at_period_end?: boolean;
  access_code_used?: string;
  access_granted_at?: string;
  /** Static routine link (full | upper | lower) chosen before signup; consumed by onboarding submit. */
  pending_routine_key?: string | null;

  // Trial
  trial_started_at?: string;
  trial_expires_at?: string;

  // Onboarding
  onboarding_completed: boolean;
  onboarding_completed_at?: string;

  // Health & Safety
  health_disclaimer_accepted: boolean;
  health_disclaimer_accepted_at?: string;

  // If org-linked (optional)
  org_data_sharing_accepted?: boolean;
  org_data_sharing_accepted_at?: string;

  // Referral tracking
  referral_source?: string;
  referral_code?: string;

  // Timestamps
  created_at: string;
  updated_at: string;
}

/**
 * Default values for new ExerciseAppProfile
 */
export const DEFAULT_EXERCISE_APP: ExerciseAppProfile = {
  subscription_status: 'none',
  onboarding_completed: false,
  health_disclaimer_accepted: false,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};




// Encounter and Clinical Note Models

export interface Encounter {
  id: string;
  created_at: string;
  updated_at: string;

  // Core relationships
  patient_id: string;
  provider_id: string;
  organization_id: string;
  location_id?: string;
  case_id?: number;

  // Encounter details
  encounter_date: string; // YYYY-MM-DD HH:MM:SS
  encounter_type: 'initial_evaluation' | 'follow_up' | 'telehealth' | 'group_session' |
  'wellness_check' | 'urgent_care' | 'consultation' | 'procedure' | 'order';
  visit_type?: 'office' | 'home' | 'telehealth' | 'facility';
  clinical_specialty?: ClinicalSpecialty;

  // Clinical information
  chief_complaint?: string;
  reason_for_visit?: string;
  presenting_problems?: string[];

  // Related records
  clinical_note_id?: string;
  prescription_ids?: string[];
  lab_order_ids?: string[];
  provider_order_ids?: string[];
  exercise_prescription_ids?: string[];
  form_response_ids?: string[];

  // Vitals captured during encounter
  vitals?: {
    blood_pressure_systolic?: number;
    blood_pressure_diastolic?: number;
    heart_rate?: number;
    temperature?: number;
    respiratory_rate?: number;
    oxygen_saturation?: number;
    height?: number;
    weight?: number;
    bmi?: number;
    pain_level?: number;
  };

  // Administrative
  appointment_id?: string;
  duration_minutes?: number;
  actual_duration_minutes?: number;
  status: 'scheduled' | 'checked_in' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';

  // Billing
  billing_codes?: {
    icd10_codes?: {
      code: string;
      description: string;
      is_primary?: boolean;
    }[];
    cpt_codes?: {
      code: string;
      description: string;
      units?: number;
      modifiers?: string[];
      /** Set only by the Billing Review Modal when the biller edits the fee; live re-pricing and the note merge leave the row's charge alone. */
      charge_override?: boolean;
      /** Direct (one-on-one) minutes; feeds the 8-minute-rule unit pool for timed codes */
      minutes?: number;
      /** Indirect/supervised minutes; counts toward total treatment time only */
      indirect_minutes?: number;
      timed?: boolean;
    }[];
  }
  copay_collected?: boolean;
  copay_amount?: number;

  // Documentation status
  documentation_status: 'pending' | 'in_progress' | 'completed' | 'signed' | 'pending_cosign';
  locked: boolean;
  locked_by?: string;
  locked_at?: string;

  // Signatures
  signed_by?: string;
  signed_at?: string;
  cosigned_by?: string;
  cosigned_at?: string;

  // Internal tracking
  no_show_reason?: string;
  cancellation_reason?: string;
  internal_notes?: string;

  // Clinical review flags (biller → clinician handoff)
  clinical_review_needed?: boolean;
  clinical_review_note?: string;
  clinical_review_resolved_at?: number;
  clinical_review_resolved_by?: string;
  counts_toward_visit_total?: boolean | null;
}

export interface ClinicalNote {
  id: string;
  encounter_id: string;
  provider_id: string;
  patient_id: string;
  created_at: string;
  updated_at: string;

  // Note metadata
  note_type: 'soap' | 'initial_evaluation' | 'progress_note' | 'daily_note' | 'discharge_summary' | 'consultation' | 'procedure_note' | 'post_operative' | 'reassessment';
  template_id?: string; // For pre-built templates
  sections_data?: import('./models/noteTemplate').NoteSectionData | null;

  // SOAP Format
  subjective: {
    // Chief complaint and history
    chief_complaint?: string;
    history_of_present_illness?: string;
    pain_description?: {
      location?: string[];
      quality?: string;
      severity?: number; // 0-10
      timing?: string;
      aggravating_factors?: string[];
      relieving_factors?: string[];
      associated_symptoms?: string[];
    };

    // Review of systems
    review_of_systems?: {
      constitutional?: string;
      cardiovascular?: string;
      respiratory?: string;
      gastrointestinal?: string;
      genitourinary?: string;
      musculoskeletal?: string;
      neurological?: string;
      psychiatric?: string;
      endocrine?: string;
      hematologic?: string;
      skin?: string;
    };

    // Functional status
    functional_status?: {
      adl_status?: string; // Activities of daily living
      mobility_status?: string;
      work_status?: string;
      exercise_tolerance?: string;
      sleep_quality?: string;
    };

    // Form integration
    form_generated_content?: string; // From form builder
    form_response_ids?: string[];
  };

  objective: {
    // Vital signs (copied from encounter)
    vitals?: {
      blood_pressure?: string;
      heart_rate?: number;
      temperature?: number;
      respiratory_rate?: number;
      oxygen_saturation?: number;
      height?: number;
      weight?: number;
      bmi?: number;
    };

    // Physical examination
    physical_exam?: {
      general?: string;
      heent?: string; // Head, eyes, ears, nose, throat
      cardiovascular?: string;
      pulmonary?: string;
      abdominal?: string;
      extremities?: string;
      neurological?: string;
      musculoskeletal?: string;
      skin?: string;
      psychiatric?: string;
    };

    // Specialized assessments
    orthopedic_tests?: {
      test_name: string;
      result: 'positive' | 'negative' | 'inconclusive';
      affected_side?: 'left' | 'right' | 'bilateral';
      notes?: string;
    }[];

    range_of_motion?: {
      joint: string;
      motion: string;
      degrees?: number;
      percentage_normal?: number;
      limited_by?: 'pain' | 'stiffness' | 'weakness' | 'other';
      notes?: string;
    }[];

    strength_testing?: {
      muscle_group: string;
      grade: string; // 0-5 or 0/5 to 5/5
      side: 'left' | 'right' | 'bilateral';
      notes?: string;
    }[];

    // Movement assessments integration
    movement_assessment_ids?: string[];
    functional_assessment_scores?: any; // Flexible for various assessment tools

    // Other findings
    laboratory_results?: string;
    imaging_results?: string;
    other_findings?: string;
  };

  assessment: {
    // Diagnoses
    diagnoses: {
      code: string; // ICD-10
      description: string;
      is_primary: boolean;
      clinical_status?: 'active' | 'resolved' | 'inactive';
      verification_status?: 'confirmed' | 'provisional' | 'differential';
      onset_date?: string;
    }[];

    // Clinical impression
    clinical_impression: string;

    // Problem list
    problems?: {
      description: string;
      status: 'active' | 'resolved' | 'improving' | 'worsening' | 'stable';
      priority: 'high' | 'medium' | 'low';
      onset_date?: string;
      notes?: string;
    }[];

    // Progress toward goals
    progress_evaluation?: string;
    barriers_to_progress?: string[];

    // Prognosis
    prognosis?: 'excellent' | 'good' | 'fair' | 'guarded' | 'poor';
    prognosis_notes?: string;
  };

  plan: {
    // Treatment plan
    treatment_plan: string;

    // Medications
    medications?: {
      name: string;
      instructions: string;
      prescription_id?: string;
      status: 'new' | 'continue' | 'modify' | 'discontinue';
    }[];

    // Procedures/Interventions
    procedures?: {
      name: string;
      cpt_code?: string;
      frequency?: string;
      duration?: string;
      special_instructions?: string;
    }[];

    // Therapeutic exercises
    exercises?: {
      exercise_prescription_id?: string;
      description?: string;
      frequency?: string;
      duration?: string;
      special_instructions?: string;
    }[];

    // Modalities
    modalities?: {
      type: string;
      parameters?: string;
      duration?: string;
      frequency?: string;
    }[];

    // Patient education
    education?: {
      topic: string;
      materials_provided?: string;
      understanding_confirmed: boolean;
      additional_resources?: string[];
    }[];

    // Activity modifications
    activity_modifications?: {
      activity: string;
      modification: string;
      duration?: string;
    }[];

    // Work/School restrictions
    work_restrictions?: {
      restriction: string;
      start_date: string;
      end_date?: string;
      notes?: string;
    }[];

    // Follow-up
    follow_up?: {
      timeframe: string;
      provider?: string;
      reason?: string;
      specific_instructions?: string;
    };

    // Referrals
    referrals?: {
      specialist_type: string;
      provider_name?: string;
      reason: string;
      urgency: 'routine' | 'urgent' | 'emergent';
      information_sent?: string;
    }[];

    // Orders
    lab_orders?: string[];
    imaging_orders?: string[];

    // Goals
    goals?: {
      description: string;
      target_date?: string;
      measurable_outcome?: string;
      status?: 'active' | 'met' | 'discontinued';
    }[];
  };

  icd10_codes: {
    code: string;
    description: string;
    is_primary?: boolean;
  }[];

  cpt_codes: {
    code: string;
    description: string;
    units?: number;
    modifiers?: string[];
    /** Direct (one-on-one) minutes; feeds the 8-minute-rule unit pool for timed codes */
    minutes?: number;
    /** Indirect/supervised minutes; counts toward total treatment time only */
    indirect_minutes?: number;
    timed?: boolean;
  }[];

  // Signatures and attestation
  signed: boolean;
  signed_by?: string;
  signed_at?: string;
  signature_attestation?: string;

  // Co-signatures (for students/residents)
  requires_cosignature?: boolean;
  cosigned?: boolean;
  cosigned_by?: string;
  cosigned_at?: string;

  // Addendums and amendments
  addendums?: {
    id: string;
    content: string;
    reason?: string;
    created_by: string;
    created_by_name?: string;
    created_at: string;
    signed: boolean;
    signed_by?: string;
    signed_at?: string;
    signature_attestation?: string;
    type?: 'text' | 'structured';
    changes?: {
      field: string;
      field_label: string;
      previous: unknown;
      current: unknown;
      type: 'soap' | 'codes' | 'template_field' | 'metadata';
    }[];
    code_corrections?: {
      icd10_codes?: { code: string; description: string; is_primary?: boolean }[];
      cpt_codes?: { code: string; description: string; units?: number; modifiers?: string[] }[];
    };
  }[];

  // Privacy and access
  access_restrictions?: string[];
  sensitive_note?: boolean;

  // Billing support
  time_spent_minutes?: number;
  complexity_level?: 'low' | 'moderate' | 'high';

  // Quality measures
  quality_measures_addressed?: string[];

  // Internal use
  internal_flags?: string[];
  qa_review_required?: boolean;
  qa_reviewed_by?: string;
  qa_reviewed_at?: string;
}


// Appointment and Message Models

export interface Appointment {
  id: string;
  created_at: string;
  updated_at: string;

  // Core relationships
  patient_id: string;
  provider_id: string;
  organization_id: string;
  location_id?: string;

  // Scheduling details
  appointment_date: string; // YYYY-MM-DD
  start_time: string; // HH:MM (24-hour format)
  end_time: string; // HH:MM (24-hour format)
  timezone: string; // IANA timezone identifier (e.g., "America/New_York")
  start_timestamp: number; // Unix timestamp in milliseconds
  end_timestamp: number; // Unix timestamp in milliseconds
  duration_minutes: number;

  // Appointment information
  appointment_type: string;
  appointment_type_id?: string; // Reference to predefined appointment types
  visit_reason?: string;
  chief_complaint?: string;

  // Telehealth
  is_telehealth: boolean;
  video_platform?: 'daily' | 'zoom' | 'other'; // For future expansion
  video_room_url?: string;
  video_room_name?: string;
  video_passcode?: string;

  // Status tracking
  status: 'scheduled' | 'confirmed' | 'requested' | 'pending_approval' | 'declined' |
  'checked_in' | 'in_room' | 'in_progress' | 'completed' | 'cancelled' | 'no_show' | 'deleted';

  // Status timestamps
  confirmed_at?: string;
  checked_in_at?: string;
  roomed_at?: string;
  with_provider_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  rescheduled_at?: string;

  // Cancellation/No-show details
  cancellation_reason?: string;
  cancelled_by?: 'patient' | 'provider' | 'staff' | 'system';
  no_show_reason?: string;

  // Reminders
  reminder_sent: boolean;
  reminder_sent_at?: string;
  reminder_method?: 'email' | 'sms' | 'both' | 'none';
  reminder_response?: 'confirmed' | 'cancelled' | 'rescheduled' | 'no_response';

  // Instructions and notes
  patient_instructions?: string;
  internal_notes?: string;
  special_requirements?: string[]; // wheelchair access, interpreter needed, etc.

  // Intake forms
  intake_forms_sent: boolean;
  intake_forms_sent_at?: string;
  intake_forms_completed: boolean;
  intake_forms_completed_at?: string;
  form_response_ids?: string[];

  // Insurance and billing
  insurance_id?: string; // Reference to patient's insurance
  insurance_verified: boolean;
  insurance_verified_at?: string;
  insurance_verified_by?: string;
  copay_amount?: number;
  copay_collected: boolean;
  copay_collected_at?: string;

  // Billing codes (preliminary)
  expected_cpt_codes?: string[];
  expected_duration_units?: number;

  // Recurring appointments
  is_recurring: boolean;
  recurrence_pattern?: {
    frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'custom';
    interval?: number; // Every X days/weeks/months
    days_of_week?: number[]; // 0-6 (Sunday-Saturday)
    day_of_month?: number; // 1-31
    end_date?: string;
    end_after_occurrences?: number;
  };
  recurrence_group_id?: string; // Links recurring appointments
  recurrence_position?: number; // Position in series

  // Waitlist
  on_waitlist: boolean;
  waitlist_priority?: number;
  earliest_acceptable_date?: string;
  latest_acceptable_date?: string;
  acceptable_times?: {
    start: string;
    end: string;
  }[];

  // Resource allocation
  room_number?: string;
  equipment_needed?: string[];
  staff_assigned?: string[]; // Additional staff IDs

  // Tagging and specialty
  tags?: string[]; // Org-configured tag IDs (not free text)
  clinical_specialty?: string;

  // Color coding (for calendar display)
  color_category?: string;

  // Clinical
  clinical_specialty?: ClinicalSpecialty;

  // Related records
  encounter_id?: string; // Created after appointment
  invoice_id?: string;
  previous_appointment_id?: string; // For follow-ups
  case_id?: number; // Links appointment to a case (episode of care)

  // Metadata
  created_by: string;
  last_modified_by?: string;
  source?: 'provider_portal' | 'patient_portal' | 'phone' | 'walk_in' | 'system';
}

interface VideoSession {
  id: string;
  created_at: number;
  appointment_id: string;
  provider_id: string;
  patient_id: string;
  encounter_id: string;
  room_name: string;
  room_url: string;
  provider_token: string;
  patient_token: string;
  scheduled_start: number;
  actual_start: number;
  actual_end: number;
  duration_seconds: number;
  status: string;
  provider_joined_at: number;
  patient_jointed_at: number;
  patient_left_at: number;
  recording_started_at: number;
  recording_enabled: boolean;
  recording_url: string;
  metadata: JSON;
}

export interface MessageThread {
  id: string;
  created_at: string;
  updated_at: string;

  // Participants
  patient_id: string;
  participants: {
    user_id: string;
    user_name?: string; // Denormalized for display
    role: 'provider' | 'patient' | 'staff' | 'admin';
    joined_at: string;
    last_read_at?: string;
    notification_preference?: 'all' | 'mentions' | 'none';
    last_read_message_id?: string;
  }[];

  // Thread information
  subject: string;
  category: 'clinical' | 'administrative' | 'billing' | 'prescription' |
  'appointment' | 'lab_results' | 'general' | 'emergency';

  // Related records
  related_encounter_id?: string;
  related_appointment_id?: string;
  related_prescription_id?: string;

  // Status
  status: 'open' | 'closed' | 'archived' | 'awaiting_response' | 'urgent';
  priority: 'low' | 'medium' | 'high' | 'urgent';

  // Assignment
  assigned_to?: string; // User ID of assigned staff/provider
  assigned_at?: string;

  // Metrics
  last_message_at: string;
  message_count: number;
  unread_count: number;
  last_sender_id: string;
  last_sender_name: string;
  last_message_preview: string;
  patient_name: string;

  // Thread settings
  patient_can_reply: boolean;
  auto_close_enabled: boolean;
  auto_close_after_days?: number;

  // Flags
  contains_attachments: boolean;
  requires_action: boolean;
  action_completed: boolean;
  action_completed_at?: string;
  action_completed_by?: string;

  // Tags for organization
  tags?: string[];

  // Internal use
  internal_notes?: string;
}

export interface Message {
  id: string;
  created_at: string;
  thread_id: string;

  // Sender information
  sender_id: string;
  sender_name?: string; // Denormalized for display
  sender_type: 'provider' | 'patient' | 'staff' | 'system';

  // Message content
  subject?: string; // Usually only for first message
  body: string;
  body_format?: 'plain' | 'markdown' | 'html';

  // Reply tracking
  parent_message_id?: string;
  is_reply: boolean;

  // Attachments
  attachments?: {
    id: string;
    filename: string;
    url: string;
    mime_type: string;
    size: number; // bytes
    uploaded_at: string;
    scan_status?: 'pending' | 'clean' | 'threat_detected';
    thumbnail_url?: string; // For images
  }[];

  // Read status
  read_receipts?: {
    user_id: string;
    read_at: string;
  }[];

  // Message metadata
  is_urgent: boolean;
  requires_response: boolean;
  response_deadline?: string;

  // Actions
  action_required?: {
    type: 'prescription_renewal' | 'form_completion' | 'appointment_scheduling' |
    'lab_review' | 'referral_approval' | 'other';
    description: string;
    completed: boolean;
    completed_by?: string;
    completed_at?: string;
  };

  // System messages
  is_system_message: boolean;
  system_message_type?: 'appointment_reminder' | 'prescription_ready' |
  'lab_results_available' | 'form_request' | 'auto_reply' | 'other';

  // Mentions
  mentioned_user_ids?: string[];

  // Edit history
  edited: boolean;
  edited_at?: string;
  edited_by?: string;
  edit_reason?: string;
  original_body?: string;

  // Delivery status
  delivery_status: 'pending' | 'sent' | 'delivered' | 'failed';
  delivery_attempts?: number;
  delivery_error?: string;

  // Security
  encrypted: boolean;
  encryption_verified: boolean;

  // Compliance
  contains_phi: boolean;
  audit_flags?: string[];

  // Templates
  template_id?: string; // If created from template
  template_variables?: Record<string, any>;

  // Auto-responses
  is_auto_response: boolean;
  auto_response_trigger?: string;

  // External notifications
  email_sent: boolean;
  email_sent_at?: string;
  sms_sent: boolean;
  sms_sent_at?: string;
  push_sent: boolean;
  push_sent_at?: string;
}

export interface MessageAttachment {
  id: string;
  created_at: number;
  message_id: string;
  thread_id: string; // Denormalized for queries
  file_name: string,
  file_size: number,
  mime_type: string,
  file_url?: string,
  file_data: object, // Xano file type
  uploaded_by: string,
  scan_status: string;
  thumbnail_url?: string
}

// Supporting interfaces for better organization

export interface AppointmentFilter {
  provider_id?: string;
  patient_id?: string;
  location_id?: string;
  status?: Appointment['status'][];
  appointment_type?: Appointment['appointment_type'][];
  date_range?: {
    start: string;
    end: string;
  };
  is_telehealth?: boolean;
}

export interface MessageFilter {
  thread_id?: string;
  patient_id?: string;
  sender_id?: string;
  category?: MessageThread['category'][];
  status?: MessageThread['status'][];
  priority?: MessageThread['priority'][];
  unread_only?: boolean;
  date_range?: {
    start: string;
    end: string;
  };
}

export interface AppointmentAvailability {
  provider_id: string;
  date: string;
  available_slots: {
    start_time: string;
    end_time: string;
    duration_minutes: number;
  }[];
  blocked_times?: {
    start_time: string;
    end_time: string;
    reason?: string;
  }[];
}

export interface ProviderAvailability {
  id: string;
  created_at: number;
  provider_id: string;
  organization_id: string;
  location_id: string;
  updated_at: number;
  schedule_template: JSON;
  schedule_overrides: JSON[];
  booking_preferences: JSON;
  is_active: boolean;
  pending_schedule_template?: JSON | null;
  schedule_template_history?: JSON[];
}

// Exercise model
export interface Exercise {
  id: string;
  created_at: string;
  updated_at: string;

  // Basic Information
  name: string; // Internal/system name
  display_name: string; // User-friendly name
  official_title?: string; // Medical/clinical title (from Rehab Prescribed)
  instructions: string; // Markdown format

  // Cardio-specific 
  is_cardio_activity?: boolean;
  cardio_type?: 'steady_state' | 'interval' | 'hiit' | 'fartlek' | 'tempo' | 'recovery';
  supports_structured_cardio?: boolean;

  // Media
  exercise_gif?: string; // URL
  video_url?: string;
  video_link?: string; // External video links
  image_url?: string;
  thumbnail_url?: string;

  // Classification & Categorization
  exercise_type: 'strength' | 'mobility' | 'cardio' | 'balance' | 'flexibility' |
  'neuromuscular' | 'plyometric' | 'recovery';
  exercise_family?: string; // e.g., "squat variations", "hip hinge"

  // Body Parts & Muscles
  body_part: string[]; // Primary body parts involved
  target_muscles?: string[]; // Primary muscles (from 'target')
  synergist_muscles?: string[]; // Supporting muscles
  stabilizer_muscles?: string[];

  // Clinical Practice Guidelines (Rehab Prescribed)
  cpg_tags?: string[]; // Specific CPG identifiers
  cpg_category?: string; // High-level CPG category
  cpg_subcategory?: string; // Detailed CPG subcategory

  // Medical/Clinical Properties
  conditions?: string[]; // Conditions this exercise helps with
  contraindications?: string[]; // When NOT to do this exercise
  precautions?: string[]; // Safety considerations
  clinical_goals?: string[]; // ROM improvement, strength, stability, etc.
  phase_appropriate?: ('acute' | 'subacute' | 'chronic' | 'return_to_sport')[];

  // Equipment
  equipment: string[]; // Consolidated from equipment1-4
  equipment_alternatives?: string[]; // Alternative equipment options
  resistance_mechanism?: 'body_weight' | 'band' | 'weight' | 'machine' | 'manual' | 'other';

  // Execution Properties
  position: string; // Starting position
  motion?: string; // Movement pattern
  movement_pattern?: 'push' | 'pull' | 'squat' | 'hinge' | 'lunge' | 'carry' | 'rotation';

  // Unilateral/Bilateral Properties
  one_sided?: boolean; // Is this a unilateral exercise?
  alternating?: boolean; // Alternating sides during execution?
  perform_both_sides?: boolean; // Must be performed on both sides?

  // Difficulty & Complexity
  difficulty_level: number; // 1-10 scale
  minimum_difficulty?: number; // Minimum fitness level required
  complexity_rating?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  skill_requirements?: string[]; // Balance, coordination, etc.

  // AI/Algorithm Properties (Ready2Exercise)
  propensity?: number; // Likelihood to be selected by AI
  eccentric_propensity?: number; // Suitability for eccentric training
  relevance?: number; // General relevance score
  hiit_suitable?: boolean; // Suitable for HIIT workouts

  // Context & Usage
  suitable_for: ('rehabilitation' | 'fitness' | 'performance' | 'wellness')[];
  setting_appropriate?: ('clinic' | 'gym' | 'home' | 'pool' | 'outdoor')[];
  supervision_required?: boolean;

  // Billing & Compliance
  cpt_code?: string; // For insurance billing
  requires_equipment_code?: string;
  medicare_covered?: boolean;

  // Metadata
  source: 'system' | 'provider_created' | 'ai_generated' | 'imported';
  created_by?: string; // User ID
  organization_id?: string; // For provider-created exercises

  // Import tracking
  legacy_id?: string; // Old ID from previous system
  import_source?: 'ready2exercise' | 'rehab_prescribed' | 'external';

  // Copyright & Licensing
  copyright_protected?: boolean;
  license_type?: 'proprietary' | 'creative_commons' | 'public_domain';
  attribution_required?: boolean;
  attribution_text?: string;

  // Quality Control
  reviewed_by?: string;
  reviewed_at?: string;
  review_status?: 'pending' | 'approved' | 'rejected' | 'needs_revision';
  quality_score?: number; // Internal quality metric

  // Usage Analytics (for algorithm improvement)
  usage_count?: number;
  completion_rate?: number;
  average_difficulty_rating?: number;

  // Search & Filter Helpers
  search_terms?: string[]; // Additional keywords for search
  tags?: string[]; // General purpose tags

  // Status
  status: 'active' | 'inactive' | 'deprecated';
  deprecated_reason?: string;
  replacement_exercise_id?: string; // If deprecated

  // Default parameter values (pre-fill when adding to flow sheets, prescriptions, etc.)
  defaults?: ExerciseDefaults | null;
}

export interface ExerciseDefaults {
  sets?: string;
  reps?: string;
  resistance?: string;
  hold_time?: string;
  rest_time?: string;
  notes?: string;
}

// Context-specific exercise prescription interfaces

export interface ExercisePrescriptionParameters {
  exercise_id: string;

  // Common parameters
  sets?: number | string; // Number or range like "2-3"
  reps?: number | string; // Number or range like "10-15"
  hold_time?: number; // seconds
  rest_time?: number; // seconds between sets

  // Rehab-specific parameters
  frequency?: string; // "3x/day", "daily", etc.
  weight?: string; // "body weight", "5 lbs", "light resistance"
  resistance_level?: string; // For bands: "light", "medium", "heavy"
  range_of_motion?: string; // "full", "partial", "pain-free"
  tempo?: string; // "2-1-2" (eccentric-pause-concentric)

  // Progression parameters
  progression_criteria?: string;
  regression_options?: string;
  target_rpe?: number; // Rate of perceived exertion (1-10)

  // Safety parameters
  pain_threshold?: string; // "0/10", "mild discomfort ok"
  special_instructions?: string;
  precautions?: string[];
  modifications?: string[];

  // Fitness-specific parameters
  intensity_level?: 'light' | 'moderate' | 'vigorous';
  heart_rate_zone?: string;
  power_output?: string;
  speed?: string;

  // Tracking
  track_weight?: boolean;
  track_pain?: boolean;
  track_difficulty?: boolean;
  track_rom?: boolean;
}

// Helper interfaces for migration

export interface ExerciseMigrationMapping {
  // Ready2Exercise field mappings
  r2e_field_map: {
    exerciseGif: 'exercise_gif';
    displayName: 'display_name';
    exerciseType: 'exercise_type';
    exerciseFamily: 'exercise_family';
    bodyPart: 'body_part';
    oneSidedAlternating: ['one_sided', 'alternating'];
    equipment1_4: 'equipment'; // Consolidate
    difficultyLevelFormula: 'difficulty_level';
    resistanceMechanism: 'resistance_mechanism';
    oldId: 'legacy_id';
    performOnBothSides: 'perform_both_sides';
    // ... etc
  };

  // Rehab Prescribed field mappings
  rp_field_map: {
    cpgTags: 'cpg_tags';
    cpgCategory: 'cpg_category';
    cpgSubcategory: 'cpg_subcategory';
    officialTitle: 'official_title';
    // ... etc
  };
}

// Filter interface for exercise library

export interface ExerciseFilter {
  // Text search
  search_query?: string;

  // Category filters
  exercise_type?: Exercise['exercise_type'][];
  body_part?: string[];
  target_muscles?: string[];
  equipment?: string[];

  // Clinical filters
  cpg_tags?: string[];
  conditions?: string[];
  phase_appropriate?: string[];

  // Difficulty filters
  difficulty_range?: {
    min: number;
    max: number;
  };

  // Context filters
  suitable_for?: ('rehabilitation' | 'fitness' | 'performance' | 'wellness')[];
  setting?: ('clinic' | 'gym' | 'home')[];
  supervision_required?: boolean;

  // Feature filters
  hiit_suitable?: boolean;
  has_video?: boolean;

  // Status
  status?: 'active' | 'inactive';
}

// Unified Exercise Prescription / Workout Model

export interface ExercisePrescription {
  id: string;
  created_at: string;
  updated_at: string;

  // Core relationships
  patient_id: string;
  provider_id?: string; // Optional for self-guided fitness
  trainer_id?: string; // For fitness programs
  organization_id: string;
  encounter_id?: string;

  // Program identification
  name: string; // "ACL Week 4-8" or "Full Body Strength Workout"
  description?: string;
  prescription_type: 'rehabilitation' | 'fitness' | 'fitness_plan' | 'wellness' | 'assessment';

  // Fitness-specific categorization
  workout_type?: 'strength' | 'cardio' | 'hiit' | 'flexibility' | 'mixed' | 'sport_specific';
  fitness_level?: 'beginner' | 'intermediate' | 'advanced' | 'elite';
  estimated_duration_minutes?: number;
  equipment_needed?: string[];

  // Context (Rehab)
  diagnosis_codes?: string[];
  clinical_goals?: string[];
  phase?: string;
  protocol_name?: string;

  // Context (Fitness)
  fitness_goals?: string[]; // "muscle_gain", "fat_loss", "endurance", "strength"
  target_muscle_groups?: string[];
  workout_split?: 'full_body' | 'upper_lower' | 'push_pull_legs' | 'body_part' | 'custom';

  // Duration and scheduling
  start_date: string;
  end_date?: string;

  // Different frequency patterns for rehab vs fitness
  schedule?: {
    // For rehabilitation
    rehab_frequency?: {
      times_per_week?: number;
      times_per_day?: number;
      specific_days?: string[];
    };

    // For fitness plans (multiple workouts)
    workout_schedule?: {
      monday?: string; // workout_id or "rest"
      tuesday?: string;
      wednesday?: string;
      thursday?: string;
      friday?: string;
      saturday?: string;
      sunday?: string;
    };

    // For single workouts
    recommended_frequency?: string; // "2-3x per week"
  };

  // Modular structure for fitness workouts
  modules?: {
    module_id: string;
    module_type: 'warmup' | 'activation' | 'main_workout' | 'cooldown' | 'stretching' | 'cardio' | 'custom';
    module_name: string;
    sequence: number;
    duration_minutes?: number;

    // Documentation requirements
    documentation_required: boolean;
    documentation_type?: 'all_exercises' | 'module_summary' | 'specific_exercises';
    specific_exercise_ids?: string[]; // If only specific exercises need documentation

    // Module-specific instructions
    instructions?: string;
    intensity_guideline?: string;

    // Exercises within module
    exercises: {
      exercise_id: string;
      sequence: number;

      // Exercise organization
      set_type: 'standard' | 'superset' | 'circuit' | 'ladder' | 'pyramid' | 'dropset' | 'amrap' | 'emom';
      group_id?: string; // For supersets/circuits
      group_position?: number; // Order within superset/circuit

      // Parameters
      parameters: ExercisePrescriptionParameters;

      // Additional fitness parameters
      rest_after_exercise?: number; // seconds
      target_heart_rate_zone?: string;
      pace?: string; // For cardio
      distance?: string; // For cardio

      // Documentation override
      documentation_required?: boolean; // Override module setting
      track_weight?: boolean;
      track_time?: boolean;
      track_distance?: boolean;
      track_heart_rate?: boolean;

      // Alternative exercises
      alternative_exercise_ids?: string[];

      // Notes
      coaching_cues?: string;
      form_notes?: string;
    }[];

    // Module completion criteria
    completion_criteria?: {
      all_exercises_required: boolean;
      minimum_exercises?: number;
      time_based?: boolean;
      target_duration?: number;
    };
  }[];

  // For simple rehab prescriptions without modules
  exercises?: {
    exercise_id: string;
    sequence: number;
    parameters: ExercisePrescriptionParameters;
    is_active: boolean;
  }[];

  // Workout/Circuit definitions (for complex set structures)
  exercise_groups?: {
    group_id: string;
    group_type: 'superset' | 'circuit' | 'complex' | 'mechanical_dropset';
    name?: string;
    instructions?: string;
    rest_between_rounds?: number;
    rounds?: number;
    time_cap?: number; // For timed circuits
  }[];

  // Global instructions
  general_instructions?: string;
  warmup_instructions?: string;
  cooldown_instructions?: string;
  precautions?: string[];

  // Progress tracking
  tracking_requirements?: {
    // Rehab-focused
    pain_scale?: boolean;
    range_of_motion?: boolean;

    // Fitness-focused
    weights_used?: boolean;
    time_completed?: boolean;
    heart_rate?: boolean;
    calories_burned?: boolean;
    rpe?: boolean;

    // Both
    sets_reps_completed?: boolean;
    form_video?: boolean;
    progress_photos?: boolean;

    // Custom metrics
    custom_metrics?: {
      name: string;
      type: 'number' | 'text' | 'boolean' | 'scale' | 'time';
      required: boolean;
      applies_to?: 'workout' | 'module' | 'exercise';
    }[];
  };

  // Completion settings
  completion_requirements?: {
    minimum_percentage?: number; // 80% of exercises
    required_modules?: string[]; // Must complete these module types
    allow_exercise_substitution?: boolean;
    allow_exercise_skipping?: boolean;
  };

  // Gamification (fitness)
  points_value?: number;
  achievement_badges?: string[];
  difficulty_rating?: number;

  // Distribution
  access_type: 'private' | 'shared' | 'public' | 'purchasable';
  share_settings?: {
    shareable_link?: string;
    requires_login?: boolean;
    expires_at?: string;
  };

  // Templates and copying
  template_id?: string;
  is_template?: boolean;
  allow_copying?: boolean;

  // Status
  status: 'draft' | 'active' | 'completed' | 'paused' | 'discontinued';

  // Analytics
  times_completed?: number;
  average_completion_time?: number;
  average_difficulty_rating?: number;

  // Billing (rehab)
  billable?: boolean;
  billing_codes?: string[];

  // Commerce (fitness)
  price?: number;
  purchase_required?: boolean;
  subscription_tier?: string;
}

// Supporting interfaces for different contexts

export type ModuleType = 'warmup' | 'activation' | 'main_workout' | 'cooldown' | 'stretching' | 'cardio' | 'custom';

export interface WorkoutModule {
  id: string;
  module_type: ModuleType;
  name: string;
  description?: string;
  typical_duration: number;
  exercise_count: number;
  is_required: boolean;
}

// not sure this aligns with how we create supersets, will need to investigate
export interface SupersetDefinition {
  id: string;
  name: string;
  exercise_ids: string[];
  rest_between_exercises: number;
  rest_between_sets: number;
  instructions?: string;
}

// not sure this aligns with how we create circuits, will need to investigate
export interface CircuitDefinition {
  id: string;
  name: string;
  exercise_sequence: {
    exercise_id: string;
    duration?: number; // seconds
    reps?: number;
    transition_time?: number;
  }[];
  rounds: number;
  rest_between_rounds: number;
  time_cap?: number;
}

export interface WorkoutPlan {
  id: string;
  created_at: number;
  name: string;
  description: string;
  plan_type: string;
  duration_weeks: number;
  fitness_level: string;
  created_by: string;
  organization_id: string;
  is_template: boolean;
  template_visibility: string;
  parent_id: string;
  updated_at: number;
}

export interface WorkoutPlanWeek {
  id: string;
  created_at: number;
  workout_plan_id: string;
  week_number: number;
  notes: string;
  updated_at: number;
}

export interface WorkoutPlanSchedule {
  id: string;
  created_at: number;
  workout_plan_week_id: string;
  exercise_prescription_id: string;
  day_of_week: number;
  is_optional: boolean;
  sequence: number;
  updated_at: number;
}

export interface RehabProgram {
  id: string;
  created_at: number;
  name: string;
  description: string;
  condition: string;
  protocol_source: string;
  total_phases: number;
  expected_duration_weeks: number;
  created_by: string;
  organization_id: string;
  is_template: boolean;
  template_visibility: string;
  parent_id: string;
  updated_at: number;
}

export interface RehabProgramPhase {
  id: string;
  created_at: number;
  rehab_program_id: string;
  phase_name: string;
  phase_number: number;
  duration_weeks: number;
  goals: string[];
  criteria_for_progression: string;
  updated_at: number;
}

export interface RehabProgramPrescription {
  id: string;
  created_at: number;
  rehab_program_phase_id: string;
  exercise_prescription_id: string;
  frequency_per_week: number;
  sequence: number;
  updated_at: number;
}

/**
 * Patient schedule override for swapping workout days
 * Used to store patient-level customizations to their workout schedule
 */
export interface PatientScheduleOverride {
  week_number: number;    // Which week (1, 2, 3...)
  original_day: number;   // ISO day (1=Mon, 7=Sun)
  new_day: number;        // ISO day to swap to
  created_at: string;     // When the override was created
}

export interface PatientExerciseAssignment {
  id: string;
  created_at: number;
  updated_at: number;

  // Relationships
  patient_id: string;
  provider_id?: string; // Optional - null for self-created
  organization_id?: string;

  // Assignment type
  assignment_type: 'workout_plan' | 'rehab_program' | 'single_prescription';
  workout_plan_id?: string;
  rehab_program_id?: string;
  exercise_prescription_id?: string;

  // Schedule
  start_date: string;
  end_date?: string;

  // Schedule overrides for day swapping (Workout Plans only)
  schedule_overrides?: PatientScheduleOverride[];

  // Status
  status: 'active' | 'completed' | 'paused' | 'discontinued' | 'draft';

  // Self-service flags (NEW)
  patient_generated: boolean; // true if created by patient, false if prescribed
  source: 'provider_assigned' | 'patient_created' | 'ai_generated' | 'template';

  // Notes
  notes?: string;
  patient_notes?: string; // Notes from the patient
}

export interface CardioCompletionData {
  exercise_id: string;
  session_id: string;
  completed_at: string;

  // Overall metrics
  total_duration_seconds?: number;
  total_distance?: number;
  distance_unit?: 'miles' | 'km' | 'meters';
  average_pace?: string;
  average_heart_rate?: number;
  calories_burned?: number;

  // For simple cardio
  simple_completion?: {
    duration_completed?: number;
    distance_completed?: number;
    intensity_actual?: 'easy' | 'moderate' | 'hard' | 'max';
    pace_actual?: string;
    heart_rate_average?: number;
  };

  // For interval/structured cardio
  segment_completions?: {
    segment_index: number;
    segment_type: 'warmup' | 'work' | 'recovery' | 'cooldown';
    completed: boolean;
    duration_actual?: number;
    distance_actual?: number;
    pace_actual?: string;
    heart_rate_average?: number;
    intensity_actual?: 'easy' | 'moderate' | 'hard' | 'max';
    notes?: string;
  }[];

  // Subjective data
  difficulty_rating?: 'too_easy' | 'just_right' | 'too_hard';
  rpe?: number; // Rate of Perceived Exertion (1-10)
  notes?: string;
}



export interface ExerciseCompletion {
  prescription_id: string;
  module_id?: string;
  exercise_id: string;
  completed_at: string;
  session_id: string;

  // Standard exercise completion
  sets_completed?: number;
  reps_completed?: number[];
  weight_used?: (number | string)[];
  time_taken?: number[];
  distance?: number[];

  // CARDIO-SPECIFIC ADDITION
  cardio_data?: CardioCompletionData;
  is_cardio?: boolean;

  // Subjective data
  rpe?: number;
  difficulty_rating?: 'too_easy' | 'just_right' | 'too_hard';
  form_rating?: number;

  // Notes
  notes?: string;
  modifications?: string;

  // Group tracking (for supersets/circuits)
  group_id?: string;
  round_number?: number;
}

// ============= CARDIO-SPECIFIC TYPES =============

// Cardio segment for intervals and structured workouts
export interface CardioSegment {
  type: 'warmup' | 'work' | 'recovery' | 'cooldown';
  duration_seconds?: number;
  distance?: number;
  distance_unit?: 'miles' | 'km' | 'meters';
  pace?: string;
  intensity?: 'easy' | 'moderate' | 'hard' | 'max';
  incline?: number;
  notes?: string;
}

// Cardio configuration structure
export interface CardioConfig {
  format: 'simple' | 'intervals' | 'structured';

  // For simple format
  simple?: {
    duration_minutes?: number;
    distance?: number;
    distance_unit?: 'miles' | 'km' | 'meters';
    target_pace?: string;
    target_heart_rate?: string;
    intensity?: 'easy' | 'moderate' | 'hard' | 'max';
  };

  // For intervals
  intervals?: {
    repeat_count?: number;
    segments: CardioSegment[];
  };

  // For structured workouts
  structured?: {
    segments: CardioSegment[];
    total_duration?: number;
  };
}

// Session state for cardio tracking
export interface CardioSessionState {
  format: 'simple' | 'intervals' | 'structured';
  current_segment_index: number;
  current_repeat: number; // For interval repeats
  segment_start_time?: number;
  total_elapsed_seconds: number;

  // Timer state
  timer_mode: 'stopped' | 'running' | 'paused' | 'rest' | 'work';
  timer_remaining: number;

  // Tracking data
  distance_covered?: number;
  current_pace?: string;
  current_heart_rate?: number;

  // Completed segments
  completed_segments: {
    segment_index: number;
    repeat_number?: number;
    duration: number;
    distance?: number;
  }[];
}


// ================= Billing Types ===============

// Billing Models

export interface Invoice {
  id: string;
  created_at: number;
  updated_at: number;
  patient_id: string;
  organization_id: string;
  encounter_id?: string;
  appointment_id?: string;

  // Invoice details
  invoice_number: string;
  invoice_date: number;
  due_date: number;
  status: 'created' | 'draft' | 'sent' | 'paid' | 'partial' | 'overdue' | 'cancelled' | 'pending_insurance' | 'refunded' | 'written_off' | 'voided' | 'consolidated';

  // Amounts
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  amount_paid: number;
  balance_due: number;

  // Manual (non-Stripe) payments recorded against the invoice
  manual_payments?: {
    amount: number;
    method: 'cash' | 'check' | 'ach' | 'zelle' | 'venmo' | 'other';
    reference?: string;
    notes?: string;
    /** Effective payment date (YYYY-MM-DD, e.g. check date); reporting prefers this over recorded_at */
    payment_date?: string | null;
    /** ISO timestamp of when the payment was entered */
    recorded_at: string;
    recorded_by: string;
    /** Index into payment_proofs */
    proof_index?: number;
    /** Offline replay guard set by the offline queue */
    client_reference?: string | null;
    /** billing_transaction id of the entry's live patient_payment fact (stamped since 2026-09-10) */
    ledger_transaction_id?: string | null;
    /** invoice_adjustment id auto-created for the remainder at record time */
    adjustment_id?: string | null;
    edited_at?: string;
    edited_by?: string;
    /** Soft void (billing/invoices/void-payment): entry keeps its index; readers skip it */
    voided_at?: string;
    voided_by?: string;
    voided_reason?: string;
  }[];

  // Line items
  line_items: {
    service_id?: string;
    service_code?: string;
    description: string;
    appointment_type_id?: string;
    quantity: number;
    unit_price: number;
    amount: number;
    tax_rate?: number;
    discount?: number;
    metadata?: {
      cpt_codes?: {
        code: string;
        description: string;
        units: number;
        modifiers?: string[];
      }[];
      icd10_codes?: {
        code: string;
        description: string;
        is_primary: boolean;
      }[];
      service_date?: string;
      provider_name?: string;
    };
  }[];

  // Payment terms
  payment_terms: 'due_on_receipt' | 'net_15' | 'net_30';
  notes?: string;

  // Stripe references
  stripe_invoice_id?: string;

  // Timestamps
  sent_at?: number;
  paid_at?: number;
  voided_at?: number;
  voided_reason?: string;

  // Insurance breakdown
  insurance_claim_id: string;
  insurance_portion?: number;
  /** Secondary payer's payment (COB); insurance paid total = insurance_portion + secondary_insurance_portion */
  secondary_insurance_portion?: number;
  patient_responsibility: number;
  copay_amount?: number;
  deductible_amount?: number;
  contractual_adjustment?: number;
  amount_collected_at_service?: number;
  adjustment_history?: {
    type: 'era_reconciliation' | 'manual_adjustment' | 'refund' | 'additional_charge';
    amount: number;
    description: string;
    adjusted_at: number;
    adjusted_by?: string;
  }[];

  // Denormalized data for display
  patient_name?: string;
  patient_email?: string;
  patient_phone?: string;
  provider_name?: string;
  organization_name?: string;
  service_date?: number;
  organization?: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    tax_id?: string;
  };
  encounter: {
    id: string;
    encounter_type?: string;
    provider_id?: string;
    provider?: {
      first_name?: string;
      last_name?: string;
      middle_name?: string;
      suffix?: string;
    }
  }
  patient: {
    first_name?: string;
    last_name?: string;
    email?: string;
  }
}

export interface Superbill {
  id: string;
  created_at: number;
  patient_id: string;
  organization_id: string;
  generated_by: string; // user_id

  // Encounters included
  encounter_ids: string[];

  // Snapshot of data at generation time
  date_range: {
    start: string;
    end: string;
  };
  total_charges: number;
  total_paid: number;

  // For future email feature
  delivery_method?: 'download' | 'email';
  emailed_to?: string;
  emailed_at?: number;

  // Optional: store PDF for re-download
  pdf_storage_path?: string;
}

export interface Payment {
  id: string;
  created_at: number;
  invoice_id?: string;
  patient_id: string;
  organization_id: string;

  // Payment details
  stripe_payment_intent_id: string;
  stripe_charge_id?: string;
  amount: number;
  currency: string;
  method: 'card' | 'ach' | 'cash' | 'check' | 'other';

  // Card details (last 4 only)
  card_last_four?: string;
  card_brand?: string;

  // Status
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'cancelled' | 'refunded';
  refunded_amount: number;

  // Fees
  platform_fee: number;
  net_amount: number;
  stripe_fee: number;

  // Metadata
  description?: string;
  metadata?: Record<string, any>;
  failure_reason?: string;
  receipt_url?: string;
  processed_at?: number;
}

export interface PaymentMethod {
  id: string;
  created_at: number;
  patient_id: string;

  // Stripe references
  stripe_payment_method_id: string;
  stripe_customer_id: string;

  // Card details
  card_brand: string;
  card_last_four: string;
  card_exp_month: number;
  card_exp_year: number;

  // Metadata
  is_default: boolean;
  billing_address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };

  // Status
  status: 'active' | 'expired' | 'deleted';
}

export interface BillingAccount {
  id: string;
  created_at: number;
  updated_at: number;
  organization_id: string;

  // Stripe Connect details
  stripe_account_id: string;
  stripe_account_type: 'express' | 'standard' | 'custom';
  onboarding_status: 'not_started' | 'pending' | 'completed' | 'rejected';
  charges_enabled: boolean;
  payouts_enabled: boolean;

  // Platform settings
  platform_fee_percentage: number;
  default_currency: string;

  // Configuration
  tax_settings?: Record<string, any>;
  payout_schedule?: string;
  statement_descriptor?: string;
}

export interface ServicePackage {
  id: string;
  created_at: number;
  organization_id: string;
  name: string;
  price: number;
  included_appointment_types: string[];
  total_credits: number;
  credit_expiration_days: number;
  stripe_product_id: string;
  stripe_price_id: string;
  active: boolean;
  never_expires: boolean;
  appointment_type_ids: string[];
  class_definition_ids?: string[];
}

export interface AppointmentType {
  id: string;
  created_at: number;
  organization_id: string;
  name: string;
  duration_minutes: number;
  color: string;
  allow_online_booking: boolean;
  allow_portal_booking: boolean;
  requires_approval: boolean;
  is_active: boolean;
  default_price: number;
  pricing_type: string;
  requires_payment: boolean;
  billable: boolean;
  typical_copay: number;
  requires_authorization: boolean;
  package_eligible: boolean;
  package_credit_value: number;
  stripe_product_id: string;
  stripe_price_id: string;
  procedure_weight?: number;
  location_ids?: string[];
  requires_documentation?: boolean;
  track_visit_time?: boolean;
  telehealth_status?: 'accepted' | 'required' | null;
  sort_order?: number;
  provider_ids?: string[];
  /** Specialties a provider must have to be offered on a patient self-service reschedule; empty = current provider's specialty. */
  clinical_specialties?: ClinicalSpecialty[];
  scribe_template_id?: string;
}

export interface PatientPackage {
  id: string;
  created_at: number;
  patient_id: string;
  package_id: string;
  purchased_date: number;
  credits_remaining: number;
  expires_date: number;
  credits_used: number;
  last_used_at: number;
}

export interface SubscriptionPlan {
  id: string; // UUID
  created_at: number; // epoch timestamp
  updated_at: number; // epoch timestamp
  organization_id: string; // UUID foreign key

  // Basic plan info
  name: string;
  description?: string;
  active: boolean;

  // Subscription type
  plan_type: 'visit_based' | 'unlimited_access';

  // Pricing
  price: number; // Decimal stored as number (e.g., 99.99)
  currency: string; // Default 'USD'
  billing_period: 'monthly' | 'quarterly' | 'yearly';

  // Visit-based plans
  credits_per_period?: number; // Number of visits/credits per billing period
  rollover_credits: boolean; // Whether unused credits roll over
  max_rollover_credits?: number; // Maximum credits that can accumulate

  // Service restrictions
  appointment_type_ids?: string[]; // Array of UUID strings - which appointment types this plan covers
  class_definition_ids?: string[]; // Array of UUID strings - which class definitions this plan covers

  // Stripe integration
  stripe_product_id?: string;
  stripe_price_id?: string;

  // Trial period
  trial_days: number; // Default 0

  // Terms
  minimum_commitment_periods: number; // Default 0 - minimum number of billing periods
  cancellation_notice_days: number; // Default 0 - days notice required for cancellation

  // Grace period for failed payments
  grace_period_days: number; // Default 7

  // Metadata
  setup_fee?: number; // One-time setup fee if applicable
  promo_code_eligible: boolean; // Whether promo codes can be applied
}

export interface InsuranceClaim {
  id: string;
  created_at: number;
  updated_at: number;

  // Relationships
  encounter_id: string;
  patient_id: string;
  organization_id: string;
  provider_id: string;

  // Payer Information
  payer_id: string; // Claim.MD payer ID
  payer_name?: string;
  insurance_plan_name?: string;
  member_id: string;
  group_number?: string;

  // Subscriber demographics (denormalized from patient insurance at claim time)
  subscriber_first_name?: string;
  subscriber_last_name?: string;
  subscriber_dob?: string;
  subscriber_gender?: string;
  subscriber_relationship?: 'self' | 'spouse' | 'child' | 'other';

  // Claim Identifiers
  remote_claim_id: string; // Our unique ID
  claimmd_id?: string; // Claim.MD's ID
  claimmd_batch_id?: string; // Batch upload ID
  payer_icn?: string; // Payer's claim number (from status/ERA)

  // Status
  status: 'draft' | 'pending_review' | 'ready_to_submit' | 'submitted' |
  'acknowledged' | 'accepted' | 'rejected' | 'denied' | 'paid' |
  'partial_paid' | 'closed';

  // Financial
  total_charged: number;
  allowed_amount?: number;
  paid_amount?: number;
  patient_responsibility?: number;
  contractual_adjustment?: number;

  // Billing Codes (denormalized from encounter/clinical_note)
  billing_codes: {
    cpt_codes: {
      code: string;
      description: string;
      units: number;
      modifiers: string[];
    }[];
    icd10_codes: {
      code: string;
      description: string;
      is_primary: boolean;
    }[];
  };

  // Claim Details
  claim_frequency?: string; // 1=original, 7=corrected, 8=void
  prior_authorization_number?: string;
  referral_number?: string;

  // Dates
  service_date: string; // YYYY-MM-DD
  statement_date_from?: string;
  statement_date_to?: string;
  submitted_at?: number;
  paid_at?: number;

  // Flags
  is_secondary_claim?: boolean;
  requires_manual_review?: boolean;
  manual_review_reason?: string;

  // Accident / Workers Comp
  accident_type?: 'workers_comp' | 'auto_accident' | 'other_accident' | 'none';
  accident_date?: string;
  accident_state?: string;
  employer_name?: string;
  workers_comp_claim_number?: string;

  // Attachments
  attachments?: {
    file_url: string;
    file_name: string;
    attachment_type: 'clinical_notes' | 'superbill' | 'lab_results' | 'referral' | 'prior_auth' | 'other';
    uploaded_at: number;
  }[];

  // Response tracking
  last_status_check?: number;
  last_response_id?: string;
  claim_data: JSON; // Full claim data sent to Claim.MD

  // Rejection/resubmission tracking
  rejection_messages?: {
    status: string;
    message: string;
    fields: string;
    mesgid: string;
  }[];
  parent_claim_id?: string;
  replaced_by_claim_id?: string;

  // Internal biller notes, appended server-side via POST /claim-notes
  notes?: import('./models/billing').ClaimNote[];

  // Populated by GET detail endpoint
  status_history?: ClaimStatusHistory[];
}

export interface ClaimStatusHistory {
  id: string;
  created_at: number;

  // Relationships
  claim_id: string;

  // Status Information
  status: string;
  messages: JSON[];
  sender_name?: string; // Who sent status (Claim.MD, Payer, etc)
  sender_id?: string;

  // Claim.MD Tracking
  response_id: string; // From Claim.MD response
  raw_response?: any; // Store full response for debugging

  // Error/Rejection Details
  is_error?: boolean;
  error_fields?: string[]; // Fields with errors
}

export interface ERA {
  id: string;
  created_at: number;
  processed_at?: number;

  // ERA Identifiers
  era_id: string; // Claim.MD ERA ID

  // Payment Information
  payment_trace_number: string;
  check_or_eft_number: string;
  payment_amount: number;
  payment_date: string; // YYYY-MM-DD
  payment_method?: string;
  vcc_number: string;
  vcc_processor: string;

  // Payer Information
  payer_id: string;
  payer_name?: string;

  // Provider Information
  provider_npi?: string;
  provider_tax_id?: string;
  provider_name?: string;

  // Organization
  organization_id: string;

  // Status
  status?: string;

  // Tracking
  downloaded?: boolean;
  downloaded_at?: number;
  raw_835_data?: string; // Store original 835 if needed

  // Totals restated by the ingest (Σ over era_detail)
  total_charged?: number;
  total_allowed?: number;
  total_patient_responsibility?: number;
  total_contractural_adjustment?: number; // sic: Xano column name
  claim_count?: number;
  details_fetched?: boolean;
  details_fetched_at?: number;

  // Origin: 'claimmd' (fetched 835) | 'manual' (paper/PDF EOB ingested through the same path)
  source?: 'claimmd' | 'manual' | string;
  // Manual source only: the EOB PDF the remit was parsed or keyed from (private attachment)
  source_file?: { path: string; name?: string; mime?: string; size?: number } | null;
}


export interface ERADetail {
  id: string;
  created_at: number;

  // Relationships
  era_id: string;
  claim_id?: string; // NULL if unmatched
  encounter_id?: string; // For easier queries
  patient_id?: string;

  // Claim Identifiers for Matching
  payer_icn?: string; // Payer's claim number
  pcn?: string; // Patient account number
  patient_name?: string;
  service_date?: string; // YYYY-MM-DD

  // Payment Status
  claim_status_code?: string; // 1=processed, 2=secondary, 3=tertiary, 4=denied

  // Financial (Encounter Level)
  total_charged?: number;
  total_allowed?: number;
  total_paid?: number;
  total_patient_responsibility?: number;
  total_contractual_adjustment?: number;

  // Adjustment Summary (claim + line CAS flattened by the ingest)
  adjustments?: {
    group_code: 'CO' | 'PR' | 'OA' | 'PI';
    reason_code: string;
    amount: number;
  }[];

  // Line-level detail in the 835/ClaimMD charge shape (also written by the
  // paper-EOB ingest); adjustment entries here use {group, code, amount}
  charges?: ERACharge[];

  // Denial Information
  is_denied?: boolean;
  denied_reasons?: string[];

  // Matching Status
  match_status?: 'unmatched' | 'auto_matched' | 'manual_matched' | 'skipped' | 'crossover_unmatched' | 'crossover_posted' | 'no_match';
  match_confidence?: number; // 0.00 to 1.00
  matched_at?: number;
  matched_by?: string; // user id
  insurance_claim_id?: string; // matched claim
}

export interface ERATracking {
  id: string;
  organization_id: string;
  last_era_id: string;
  last_checked_at: number;
  updated_at: number;
}

export interface ClaimStatusTracking {
  id: string;
  organization_id: string;
  last_response_id: string;
  last_checked_at: number;
  updated_at: number;
}

// ================= Fee Schedule Types =================

export interface FeeSchedule {
  id: string;
  created_at: number;
  updated_at: number;
  organization_id: string;
  payer_id: string; // Claim.MD payer ID
  payer_name: string;
  cpt_code: string;
  description: string;
  contracted_rate: number;
  effective_date: string; // YYYY-MM-DD
  expiration_date?: string; // YYYY-MM-DD, nullable
  modifier?: string; // For modifier-specific rates
}


// ================= RTM (Remote Therapeutic Monitoring) Types ===============

/**
 * RTM Enrollment - Links a patient's rehab program assignment to RTM billing
 */
export interface RTMEnrollment {
  id: string;
  created_at: number;

  // Relationships
  patient_id: string;
  patient_exercise_assignment_id: string;  // FK to rehab program assignment
  organization_id: string;
  enrolled_by: string;                     // Provider user_id

  // Setup tracking (CPT 98975)
  enrolled_at: number;
  setup_education_completed: boolean;
  setup_education_completed_at?: number;
  setup_billed: boolean;

  // Payer info
  payer_id?: string;
  payer_coverage_status: 'covered' | 'not_covered' | 'unverified';

  // Status
  status: 'active' | 'paused' | 'completed' | 'discontinued';
  discontinued_at?: number;
  discontinued_reason?: string;

  notes?: string;

  // Denormalized for display
  patient_name?: string;
  program_name?: string;
}

/**
 * RTM Billing Period - Auto-generated for each calendar month an enrollment is active
 */
export interface RTMBillingPeriod {
  id: string;
  created_at: number;

  rtm_enrollment_id: string;

  // Period bounds (calendar month)
  period_start: string;                    // YYYY-MM-DD (1st of month or enrollment date)
  period_end: string;                      // YYYY-MM-DD (last day of month)

  // Computed aggregates
  transmission_day_count: number;          // Unique days with data transmission
  management_minutes_total: number;        // Sum of provider time
  interactive_communication_completed: boolean;

  // Billing eligibility (computed)
  device_code_eligible: '98985' | '98977' | null;   // Based on day count
  management_code_eligible: '98979' | '98980' | null; // Based on minutes

  // Billing status
  billed_codes: string[];                  // CPT codes actually billed
  claim_id?: string;                       // FK to insurance_claims
  billed_at?: number;
  status: 'open' | 'ready_to_bill' | 'billed' | 'closed';
}

/**
 * RTM Transmission Log - Tracks each day's data transmission for day-count calculation
 * Note: Multiple transmissions on the same date only count as 1 transmission day
 */
export interface RTMTransmissionLog {
  id: string;
  created_at: number;

  rtm_enrollment_id: string;
  rtm_billing_period_id: string;

  transmission_date: string;               // YYYY-MM-DD
  transmission_type: 'session' | 'checkin' | 'app_interaction' | 'message';

  // Source reference (for audit trail)
  source_type?: 'exercise_session' | 'sms_reply' | 'app_login' | 'message';
  source_id?: string;                      // ID of the source record
}

/**
 * RTM Management Activity - Tracks provider time spent on RTM activities
 */
export interface RTMManagementActivity {
  id: string;
  created_at: number;

  rtm_enrollment_id: string;
  rtm_billing_period_id: string;

  // Provider info
  provider_id: string;
  provider_designation?: 'PT' | 'PTA' | 'OT' | 'OTA' | 'SLP' | 'SLPA' | 'MD' | 'DO' | 'NP' | 'PA' | 'DC';

  // Activity details
  activity_type: 'data_review' | 'care_plan_update' | 'documentation' | 'communication';
  duration_seconds: number;

  started_at: number;
  ended_at: number;

  notes?: string;
}

/**
 * RTM Interaction - Tracks interactive communications (required for management codes)
 */
export interface RTMInteraction {
  id: string;
  created_at: number;

  rtm_enrollment_id: string;
  rtm_billing_period_id: string;

  interaction_type: 'phone' | 'video' | 'in_person';
  interaction_date: string;                // YYYY-MM-DD
  duration_minutes: number;

  provider_id: string;
  appointment_id?: string;                 // Auto-link from telehealth appointments

  notes?: string;
  qualifies_for_rtm_billing: boolean;
}

/**
 * RTM Alert - Used for dashboard alerts and notifications
 */
export interface RTMAlert {
  id: string;
  type: 'threshold_approaching' | 'missing_interaction' | 'payer_warning' | 'month_ending' | 'ready_to_bill';
  severity: 'info' | 'warning' | 'urgent';
  title: string;
  message: string;
  enrollment_id: string;
  patient_id: string;
  patient_name?: string;
  created_at: number;
  action_url?: string;
}

/**
 * RTM Dashboard Summary - Aggregated RTM metrics for organization
 */
export interface RTMDashboardSummary {
  active_enrollments: number;
  ready_to_bill: number;
  missing_interaction: number;
  estimated_revenue: number;
  alerts: RTMAlert[];
}

/**
 * RTM CPT Code - Reference for RTM billing codes
 */
export interface RTMCPTCode {
  code: '98975' | '98985' | '98977' | '98979' | '98980' | '98981';
  description: string;
  rate_2026: number;
  threshold_description: string;
  modifiers: string[];
}

/**
 * Provider designation types for modifier tracking
 */
export type ProviderDesignation = 'PT' | 'PTA' | 'SPT' | 'OT' | 'OTA' | 'SOT' | 'SLP' | 'SLPA' | 'SSLP' | 'MD' | 'DO' | 'NP' | 'PA' | 'DC' | 'LMT' | 'CMT' | 'RMT' | 'PT_AIDE' | 'OT_AIDE' | 'TECH';


// ============================================
// PATIENT LOCATION / GYM TYPES
// ============================================

/**
 * Location type for workout locations
 */
export type PatientLocationType = 'home' | 'commercial_gym' | 'workplace' | 'outdoor' | 'clinic' | 'other';

/**
 * PatientLocation - Represents a workout location with available equipment
 * Can be patient-specific (personal) or shared/public (e.g., commercial gyms)
 */
export interface PatientLocation {
  id: string;
  created_at: string;
  updated_at: string;

  // Ownership - one of these should be set
  patient_id?: string;           // If patient-specific (personal location)
  organization_id?: string;      // For shared/public locations

  // Visibility
  is_public: boolean;            // true = shared gym available to all patients

  // Basic Info
  name: string;                  // "Home", "Planet Fitness - Main St", "Work Gym"
  location_type: PatientLocationType;

  // Optional Address (primarily for commercial gyms)
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
  };

  // Equipment - uses same items as EQUIPMENT_CATEGORIES in RoutineBuilder
  equipment: string[];           // ["Dumbbell", "Resistance Band", "Pull Up Bar", etc.]
  equipment_categories?: string[]; // ["Free Weights", "Resistance Bands & Cables", etc.]

  // Additional Metadata
  notes?: string;                // "Open 24 hours", "No barbells on weekends", etc.

  // User preferences
  is_favorite: boolean;          // Quick access marker
  is_default: boolean;           // Default selection for sessions
  last_used_at?: string;         // For sorting by recent use

  // Status
  status: 'active' | 'archived';
}

/**
 * PatientLocationFavorite - Junction table for patients to save/favorite public gyms
 */
export interface PatientLocationFavorite {
  id: string;
  created_at: string;
  patient_id: string;
  location_id: string;           // References PatientLocation (public gym)
  is_default: boolean;           // If this is the patient's default location
  nickname?: string;             // Patient's custom name for this location
}

/**
 * SessionLocationContext - Tracks the location context for a workout session
 */
export interface SessionLocationContext {
  location_id: string;
  location_name: string;
  location_type: PatientLocationType;
  equipment_available: string[];
  equipment_gaps: string[];      // Required equipment not available at this location
  substitutions: ExerciseSubstitution[];
}

/**
 * ExerciseSubstitution - Represents a substituted exercise due to equipment availability
 */
export interface ExerciseSubstitution {
  original_exercise_id: string;
  original_exercise_name: string;
  substitute_exercise_id: string;
  substitute_exercise_name: string;
  substitution_reason: 'equipment_unavailable' | 'patient_preference' | 'difficulty_adjustment';
  equipment_required: string[];  // Equipment the original exercise needed
  equipment_missing: string[];   // Equipment that was not available
  match_score?: number;          // 0-100 similarity score
}

/**
 * ExerciseEquipmentCheck - Result of checking exercise equipment compatibility
 */
export interface ExerciseEquipmentCheck {
  exercise_id: string;
  exercise_name: string;
  equipment_required: string[];
  equipment_available: string[];
  equipment_missing: string[];
  is_compatible: boolean;
  has_alternatives: boolean;     // Whether substitutes exist
}

/**
 * FindSubstitutesRequest - Request payload for finding exercise substitutes
 */
export interface FindSubstitutesRequest {
  exercise_id: string;
  available_equipment: string[];
  difficulty_tolerance?: number; // How much difficulty variance allowed (default: 2)
  include_bodyweight?: boolean;  // Include bodyweight alternatives (default: true)
  limit?: number;                // Max substitutes to return (default: 5)
}

/**
 * SubstituteMatch - A potential substitute exercise with match details
 */
export interface SubstituteMatch {
  exercise: Exercise;
  match_score: number;           // 0-100 based on similarity
  match_reasons: string[];       // ["Same exercise family", "Same movement pattern"]
  equipment_used: string[];      // From available equipment
}

/**
 * FindSubstitutesResponse - Response from finding exercise substitutes
 */
export interface FindSubstitutesResponse {
  original_exercise: Exercise;
  equipment_required: string[];
  equipment_missing: string[];
  substitutes: SubstituteMatch[];
}

